import { useCallback, useState, useEffect } from 'react';
import { Command } from 'cmdk';
import { Loader2, AlertCircle } from 'lucide-react';
import { useCommandPalette } from '../hooks/useCommandPalette';
import { useCommands } from '../hooks/useCommands';
import { addToRecentCommands } from '../lib/commandPaletteUtils';
import type { CommandGroup } from '../types/commands';

interface CommandPaletteProps {
  openInviteModal: () => void;
}

const GROUP_LABELS: Record<CommandGroup, string> = {
  recent: 'Recent',
  navigation: 'Navigation',
  actions: 'Actions',
  workflows: 'Workflows',
  components: 'Task Library',
  settings: 'Settings',
  admin: 'Admin',
};

const GROUP_ORDER: Array<CommandGroup> = [
  'recent',
  'navigation',
  'actions',
  'workflows',
  'components',
  'settings',
  'admin',
];

/**
 * Main command palette component using cmdk library
 * Provides unified keyboard-first navigation and actions
 */
export function CommandPalette({ openInviteModal }: CommandPaletteProps) {
  const { isOpen, close } = useCommandPalette();
  const [searchQuery, setSearchQuery] = useState('');

  const { staticCommands, dynamicCommands, recentCommands, isLoading, errors } = useCommands(
    searchQuery,
    { openInviteModal },
  );

  // Reset search query when palette closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
    }
  }, [isOpen]);

  const handleSelect = useCallback(
    (command: (typeof staticCommands)[number]) => {
      void command.action();
      addToRecentCommands(command);
      close();
    },
    [close],
  );

  // Organize commands by group
  const commandsByGroup = useCallback(() => {
    const allCommands = [...recentCommands, ...staticCommands, ...dynamicCommands];
    const groupMap = new Map<CommandGroup, typeof allCommands>();

    allCommands.forEach((cmd) => {
      if (!groupMap.has(cmd.group)) {
        groupMap.set(cmd.group, []);
      }
      const group = groupMap.get(cmd.group);
      if (group) {
        group.push(cmd);
      }
    });

    // Return groups in the specified order
    const orderedGroups: Array<[CommandGroup, typeof allCommands]> = [];
    GROUP_ORDER.forEach((groupKey) => {
      const commands = groupMap.get(groupKey);
      if (commands) {
        orderedGroups.push([groupKey, commands]);
      }
    });

    return orderedGroups;
  }, [recentCommands, staticCommands, dynamicCommands]);

  if (!isOpen) {
    return null;
  }

  const groups = commandsByGroup();

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events -- click-outside-to-close backdrop; Escape is handled on the panel below
    <div className="fixed inset-0 z-50 bg-black/50" onClick={close}>
      <div className="flex items-start justify-center pt-[15vh]">
        <div
          className="w-full max-w-2xl rounded-lg border border-zinc-700 bg-zinc-800 shadow-2xl"
          onClick={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.key === 'Escape' && close()}
        >
          <Command shouldFilter={false}>
            <div className="flex items-center border-b border-zinc-700 px-4">
              <Command.Input
                placeholder="Search commands..."
                value={searchQuery}
                onValueChange={setSearchQuery}
                className="h-14 w-full bg-transparent text-white placeholder-zinc-400 outline-hidden"
              />
            </div>

            <Command.List className="max-h-[400px] overflow-y-auto p-2">
              {isLoading && searchQuery.length > 2 && (
                <div className="flex items-center justify-center py-8 text-zinc-400">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span>Searching...</span>
                </div>
              )}

              {errors.workflows && (
                <div className="flex items-center px-4 py-2 text-sm text-red-400">
                  <AlertCircle className="mr-2 h-4 w-4" />
                  <span>Failed to search workflows</span>
                </div>
              )}

              {errors.components && (
                <div className="flex items-center px-4 py-2 text-sm text-red-400">
                  <AlertCircle className="mr-2 h-4 w-4" />
                  <span>Failed to search components</span>
                </div>
              )}

              {!isLoading &&
                groups.map(([group, commands]) => (
                  <Command.Group
                    key={group}
                    heading={GROUP_LABELS[group]}
                    className="**:[[cmdk-group-heading]]:px-2 **:[[cmdk-group-heading]]:py-2 **:[[cmdk-group-heading]]:text-xs **:[[cmdk-group-heading]]:font-medium **:[[cmdk-group-heading]]:text-zinc-400"
                  >
                    {commands.map((cmd) => {
                      const Icon = cmd.icon;
                      return (
                        <Command.Item
                          key={cmd.id}
                          value={cmd.label}
                          onSelect={() => handleSelect(cmd)}
                          className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-white aria-selected:bg-sky-600 data-[selected=true]:bg-sky-600"
                        >
                          <Icon className="h-4 w-4" />
                          <span>{cmd.label}</span>
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                ))}

              {!isLoading && groups.length === 0 && (
                <Command.Empty className="py-8 text-center text-sm text-zinc-400">
                  No commands found
                </Command.Empty>
              )}
            </Command.List>
          </Command>
        </div>
      </div>
    </div>
  );
}
